import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { UserContext } from '../App';
import {
  Typography, DatePicker, Select, Button, Table, Form, Tooltip, message, Segmented, Checkbox,
} from 'antd';
import { SearchOutlined, DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import ResizableTitle from '../components/ResizableTitle';
import ErlangTrend from '../components/ErlangTrend';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const tip = (label, formula) => (
  <span>
    {label}&nbsp;
    <Tooltip title={formula} overlayStyle={{ maxWidth: 400 }}>
      <QuestionCircleOutlined style={{ fontSize: 11, color: '#8c8c8c', cursor: 'help' }} />
    </Tooltip>
  </span>
);

const LOOKBACK_OPTIONS = [1,2,3,4,6,8,12,16,20,24,28,32,36,40,44,48,52].map((v) => ({ label: String(v), value: v }));

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  label: i === 0 ? '12AM' : i < 12 ? `${i}AM` : i === 12 ? '12PM' : `${i - 12}PM`,
  value: i,
}));

const DAY_OPTIONS = [
  { label: 'Sunday',    value: 1 },
  { label: 'Monday',    value: 2 },
  { label: 'Tuesday',   value: 3 },
  { label: 'Wednesday', value: 4 },
  { label: 'Thursday',  value: 5 },
  { label: 'Friday',    value: 6 },
  { label: 'Saturday',  value: 7 },
];

const fmtHour = (h) => {
  if (h == null) return '—';
  if (h === 0)   return '12AM';
  if (h < 12)    return `${h}AM`;
  if (h === 12)  return '12PM';
  return `${h - 12}PM`;
};

const fmtHalfHour = (v) => {
  if (v == null) return '—';
  const h = Math.floor(v / 100);
  const m = v % 100;
  const period = h < 12 ? 'AM' : 'PM';
  const dispH  = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0
    ? `${dispH}:00 ${period}`
    : `${dispH}:${String(m).padStart(2, '0')} ${period}`;
};

const n  = (v) => (v == null ? '—' : v);
const d1 = (v) => (v == null ? '—' : Number(v).toFixed(1));
const d2 = (v) => (v == null ? '—' : Number(v).toFixed(2));
const p1 = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const p0    = (v) => (v == null ? '—' : `${Math.round(Number(v))}%`);
const p0r01 = (v) => (v == null ? '—' : `${Math.round(Number(v) * 100)}%`);

// seconds → M:SS or H:MM:SS (hours hidden when 0)
const fmtMMSS = (totalSecs) => {
  if (totalSecs == null) return '—';
  const t = Math.round(Number(totalSecs));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};
// AHT comes in as minutes — convert to seconds first
const fmtAHT = (v) => fmtMMSS(v != null ? Number(v) * 60 : null);

const num = (v) => Number(v) || 0;

export default function ErlangStatsPage() {
  const _SESSION_KEY = 'erlang-stats';
  const _loadErlang  = () => {
    try {
      const raw = sessionStorage.getItem(_SESSION_KEY);
      if (!raw) return null;
      const v = JSON.parse(raw);
      if (v.dateRange) v.dateRange = v.dateRange.map((d) => (d ? dayjs(d) : null));
      return v;
    } catch { return null; }
  };
  const currentUser = useContext(UserContext);
  const _saved = _loadErlang();

  const [dateRange,    setDateRange]    = useState(_saved?.dateRange   ?? [dayjs().startOf('month'), dayjs().subtract(1, 'day')]);
  const [campaign,     setCampaign]     = useState(_saved?.campaign    ?? null);
  const [callHours,    setCallHours]    = useState(_saved?.callHours   ?? []);
  const [dayOfWeek,    setDayOfWeek]    = useState(_saved?.dayOfWeek   ?? []);
  const [isHoliday,    setIsHoliday]    = useState(_saved?.isHoliday   ?? '0');
  const [campaignOpts,    setCampaignOpts]    = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // Filter Erlang campaign list by user's visible campaigns (substring match).
  // Admins and users with no campaign restrictions see everything.
  const filteredCampaignOpts = useMemo(() => {
    const role  = currentUser?.Role;
    const camps = currentUser?.Campaigns || [];
    if (!currentUser || ['admin', 'qa_admin'].includes(role) || camps.length === 0) return campaignOpts;
    const lower = camps.map(c => c.toLowerCase());
    return campaignOpts.filter(opt => lower.some(vc => opt.value.toLowerCase().includes(vc)));
  }, [campaignOpts, currentUser]);
  // Only one campaign: picked, as on the other Interactions pages.
  useEffect(() => {
    if (!campaign && filteredCampaignOpts.length === 1) setCampaign(filteredCampaignOpts[0].value);
  }, [filteredCampaignOpts]); // eslint-disable-line react-hooks/exhaustive-deps
  const [standardRows,     setStandardRows]     = useState([]);
  const [analysisRows,     setAnalysisRows]     = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [colWidths,        setColWidths]        = useState({});
  const [activeView,       setActiveView]       = useState(_saved?.activeView ?? 'standard');
  const [analysisDate,     setAnalysisDate]     = useState(null);
  const [lookbackWeeks,    setLookbackWeeks]    = useState(6);
  const [lookbackRows,     setLookbackRows]     = useState([]);
  const [hideAverages,     setHideAverages]     = useState(false);
  const [trendData,        setTrendData]        = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchWithRetry = (attempt) => {
      if (cancelled) return;
      setCampaignsLoading(true);
      api.get('/api/erlang-stats/campaigns')
        .then((r) => {
          if (!cancelled) {
            setCampaignOpts(r.data.map((c) => ({ label: c, value: c })));
            setCampaignsLoading(false);
          }
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 3) {
            setTimeout(() => fetchWithRetry(attempt + 1), 1500 * attempt);
          } else {
            setCampaignsLoading(false);
            message.error('Could not load campaign list — please refresh the page.');
          }
        });
    };
    fetchWithRetry(1);
    return () => { cancelled = true; };
  }, []);

  const toResizable = useCallback((col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 100,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  }), [colWidths]);

  const handleExport = () => {
    const COLS = [
      { header: 'Campaign',           get: (r) => r.CampaignName },
      { header: 'Hour',               get: (r) => fmtHour(r.HourNum) },
      { header: 'Half Hour',          get: (r) => fmtHalfHour(r.HalfHourOfDay) },
      { header: 'Days',               get: (r) => r.Days ?? null },
      { header: 'Avg Offered',        get: (r) => r.AvgOffered != null ? Number(r.AvgOffered) : null },
      { header: 'Raw SLA %',          get: (r) => r.RawSLA != null ? Number(r.RawSLA) : null },
      { header: 'Abandon %',          get: (r) => r.AbandonPct != null ? Number(r.AbandonPct) : null },
      { header: 'Raw ASA',             get: (r) => r.RawASA != null ? Number(r.RawASA) : null },
      { header: '% of Offered',       get: (r) => r.PctOfOffered != null ? Number(Number(r.PctOfOffered).toFixed(1)) : null },
      { header: 'Occupancy %',        get: (r) => r.OccupancyPct   != null ? Number(r.OccupancyPct)   : null },
      { header: 'InAvailable %',           get: (r) => r.InAvailablePct != null ? Number(r.InAvailablePct) : null },
      { header: 'Avg Shrink %',       get: (r) => r.AvgShrinkPct != null ? Number(r.AvgShrinkPct) : null },
      { header: 'Avg Staffed',        get: (r) => r.AvgStaffed != null ? Number(r.AvgStaffed) : null },
      { header: 'Avg IC 4SLA',        get: (r) => r.AvgIC4SLA != null ? Number(r.AvgIC4SLA) : null },
      { header: 'Erlang FTE',         get: (r) => r.ErlangFTE != null ? Number(Number(r.ErlangFTE).toFixed(2)) : null },
      { header: 'Erlang Shrink FTE',  get: (r) => r.ErlangShrinkageFTE != null ? Number(Number(r.ErlangShrinkageFTE).toFixed(2)) : null },
      { header: 'Target SLA %',       get: (r) => r.TargetSLA != null ? Math.round(Number(r.TargetSLA) * 100) : null },
      { header: 'Target Time (s)',     get: (r) => r.TargetTime ?? null },
      { header: 'Max Offered',        get: (r) => r.MaxOffered ?? null },
      { header: 'Raw AHT',            get: (r) => r.RawAHT != null ? Number(r.RawAHT) : null },
      { header: 'Avg Proj Email',     get: (r) => r.AvgProjEmailSupport != null ? Number(r.AvgProjEmailSupport) : null },
      { header: 'Avg Break/Lunch',    get: (r) => r.AvgBreakLunch != null ? Number(r.AvgBreakLunch) : null },
      { header: 'Avg Not Logged In',  get: (r) => r.AvgNotLoggedIn != null ? Number(r.AvgNotLoggedIn) : null },
      { header: 'Avg Tech',           get: (r) => r.AvgTech != null ? Number(r.AvgTech) : null },
      { header: 'Avg Meet/Coach',     get: (r) => r.AvgMeetCoach != null ? Number(r.AvgMeetCoach) : null },
      { header: 'Abandon LT 5s',      get: (r) => r.AbandonLT5Sec ?? null },
      { header: 'Short Abandon',      get: (r) => r.ShortAbandon ?? null },
      { header: 'Total Offered',      get: (r) => r.TotalOffered ?? null },
      { header: 'Total Handled',      get: (r) => r.TotalHandled ?? null },
      { header: 'Handled In SLA',     get: (r) => r.TotalHandledInSLA ?? null },
      { header: 'Total Abandon',      get: (r) => r.TotalAbandon ?? null },
    ];
    const ws = XLSX.utils.aoa_to_sheet([
      COLS.map((c) => c.header),
      ...processedRows.map((r) => COLS.map((c) => c.get(r))),
    ]);
    ws['!cols'] = COLS.map((c) => ({ wch: Math.max(c.header.length, 10) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Erlang Stats');
    const camp = campaign ? campaign.replace(/[/\\?*[\]:]/g, '_').slice(0, 20) : 'export';
    XLSX.writeFile(wb, `erlang-stats-${camp}.xlsx`);
  };

  const doSearch = useCallback((params, setter) => {
    setLoading(true);
    api.get('/api/erlang-stats', { params })
      .then((r) => setter(r.data))
      .catch(() => setter([]))
      .finally(() => setLoading(false));
  }, []);

  const _saveErlang = () => {
    try {
      const v = {
        campaign, callHours, dayOfWeek, isHoliday,
        dateRange: dateRange?.map((d) => (d ? d.format('YYYY-MM-DD') : null)),
      };
      sessionStorage.setItem(_SESSION_KEY, JSON.stringify(v));
    } catch {}
  };

  const handleSearch = () => {
    _saveErlang();
    const params = {};
    if (dateRange?.[0]) params.start_date  = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) params.end_date    = dateRange[1].format('YYYY-MM-DD');
    if (campaign)          params.campaign    = campaign;
    if (callHours.length)  params.call_hour   = callHours.join(',');
    if (dayOfWeek.length)  params.day_of_week = dayOfWeek.join(',');
    if (isHoliday !== 'all') params.is_holiday = isHoliday;
    if (activeView === 'trend') {
      // Trend: the same filters, one row per date, beside last year's.
      setLoading(true);
      api.get('/api/erlang-stats/by-day', { params })
        .then((r) => setTrendData(r.data))
        .catch(() => { setTrendData(null); message.error('Could not load the trend.'); })
        .finally(() => setLoading(false));
      return;
    }
    doSearch(params, setStandardRows);
  };

  const handleAnalysisSearch = (d, wks) => {
    const date  = d   || analysisDate;
    const weeks = wks ?? lookbackWeeks;
    if (!date || !campaign) return;
    const ds = date.format('YYYY-MM-DD');
    doSearch({ campaign, start_date: ds, end_date: ds }, setAnalysisRows);
    setLookbackRows([]);
    api.get('/api/erlang-stats/lookback', { params: { campaign, date: ds, weeks } })
      .then((r) => setLookbackRows(r.data))
      .catch(() => setLookbackRows([]));
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    try {
      const raw = sessionStorage.getItem(_SESSION_KEY);
      const existing = raw ? JSON.parse(raw) : {};
      sessionStorage.setItem(_SESSION_KEY, JSON.stringify({ ...existing, activeView: view }));
    } catch {}
    if (view === 'date-analysis') {
      const d = dateRange?.[1] ?? dayjs();
      setAnalysisDate(d);
      if (campaign) handleAnalysisSearch(d, lookbackWeeks);
    }
  };

  const processedRows = useMemo(() => {
    const totals = {};
    standardRows.forEach((r) => {
      totals[r.CampaignName] = (totals[r.CampaignName] || 0) + num(r.AvgOffered);
    });
    return standardRows.map((r, i) => ({
      ...r,
      _key: i,
      PctOfOffered: totals[r.CampaignName] > 0
        ? (num(r.AvgOffered) / totals[r.CampaignName]) * 100
        : null,
    }));
  }, [standardRows]);

  const ANALYSIS_METRICS = [
    { label: 'Raw SLA',     field: 'RawSLA',       fmt: p0 },
    { label: 'Staffed',     field: 'AvgStaffed',   fmt: d1 },
    { label: 'IC 4SLA',     field: 'AvgIC4SLA',    fmt: d1 },
    { label: 'Offered',     field: 'TotalOffered', fmt: n  },
    { label: 'Abandons',    field: 'TotalAbandon', fmt: n  },
    { label: 'ASA',         field: 'RawASA',       fmt: fmtMMSS },
    { label: 'Raw AHT',     field: 'RawAHT',       fmt: fmtAHT  },
    { label: 'Occupancy %',        field: 'OccupancyPct',        fmt: p1 },
    { label: 'InAvailable %',          field: 'InAvailablePct',      fmt: p1 },
    { label: 'Avg Not Logged In',  field: 'AvgNotLoggedIn',      fmt: d2 },
    { label: 'Avg Proj Email',     field: 'AvgProjEmailSupport', fmt: d2 },
    { label: 'Avg Break/Lunch',    field: 'AvgBreakLunch',       fmt: d2 },
    { label: 'Avg Tech',           field: 'AvgTech',             fmt: d2 },
    { label: 'Avg Meet/Coach',     field: 'AvgMeetCoach',        fmt: d2 },
    { label: 'Had Available',      field: 'HadAvailable',        fmt: n  },
    { label: 'Zero Available',     field: 'ZeroAvailable',       fmt: n  },
    { label: 'Max Queued',         field: 'CampaignMaxQueued',   fmt: n  },
    { label: 'Avg Queue Size',     field: 'AvgQueueSize',        fmt: d2 },
  ];

  const analysisData = useMemo(() => {
    if (!analysisRows.length) return { cols: [], data: [] };
    const halfHours = [...new Set(analysisRows.map((r) => r.HalfHourOfDay))].sort((a, b) => a - b);
    const lookup = {};
    analysisRows.forEach((r) => { lookup[r.HalfHourOfDay] = r; });
    const lbLookup = {};
    lookbackRows.forEach((r) => { lbLookup[r.HalfHourOfDay] = r; });
    const hasLookback = lookbackRows.length > 0 && !hideAverages;

    const cols = [
      { key: '_metric', title: 'Metric', dataIndex: '_metric', width: 170, fixed: 'left',
        onCell: () => ({ style: { fontWeight: 600 } }),
        render: (text) => (
          <div style={{ lineHeight: 1.3 }}>
            <div>{text}</div>
            {hasLookback && (
              <div style={{ color: '#8c8c8c', fontSize: 11, fontStyle: 'italic', fontWeight: 400 }}>
                DOW Avg Last {lookbackWeeks} wks
              </div>
            )}
          </div>
        ),
      },
      ...halfHours.map((hh) => ({
        key: String(hh), title: fmtHalfHour(hh), dataIndex: String(hh),
        width: 85, align: 'right',
        onCell: (record) => {
          if (record._key !== 'RawSLA') return {};
          const cell = record[String(hh)];
          if (!cell || cell.rawSLA == null || cell.target == null) return {};
          return { style: { background: cell.rawSLA >= cell.target ? '#f6ffed' : '#fff1f0' } };
        },
        render: (cell) => {
          if (!cell) return '—';
          return (
            <div style={{ lineHeight: 1.3 }}>
              <div>{cell.cur}</div>
              {hasLookback && <div style={{ color: '#8c8c8c', fontSize: 11, fontStyle: 'italic' }}>{cell.avg}</div>}
            </div>
          );
        },
      })),
    ];

    const data = ANALYSIS_METRICS.map((m) => {
      const row = { _key: m.field, _metric: m.label };
      halfHours.forEach((hh) => {
        const cur = lookup[hh];
        const lb  = lbLookup[hh];
        row[String(hh)] = {
          cur:    cur != null ? m.fmt(cur[m.field]) : '—',
          avg:    lb  != null ? m.fmt(lb[m.field])  : '—',
          rawSLA: m.field === 'RawSLA' && cur?.RawSLA    != null ? Number(cur.RawSLA)            : null,
          target: m.field === 'RawSLA' && cur?.TargetSLA != null ? Number(cur.TargetSLA) * 100   : null,
        };
      });
      return row;
    });

    return { cols, data };
  }, [analysisRows, lookbackRows, lookbackWeeks, hideAverages]);

  const columns = useMemo(() => [
    { key: 'CampaignName',        title: 'Campaign',           dataIndex: 'CampaignName',        width: 160, fixed: 'left', render: n,  sorter: (a, b) => String(a.CampaignName).localeCompare(String(b.CampaignName)) },
    { key: 'HourOfDay',           title: 'Hour',               dataIndex: 'HourNum',             width: 70,  fixed: 'left', render: fmtHour,     sorter: (a, b) => num(a.HourNum) - num(b.HourNum) },
    { key: 'HalfHourOfDay',       title: 'Half Hour',          dataIndex: 'HalfHourOfDay',       width: 100, fixed: 'left', render: fmtHalfHour, sorter: (a, b) => num(a.HalfHourOfDay) - num(b.HalfHourOfDay) },
    { key: 'AvgOffered',          title: 'Avg Offered',        dataIndex: 'AvgOffered',          width: 100, render: d2,    sorter: (a, b) => num(a.AvgOffered) - num(b.AvgOffered) },
    { key: 'RawSLA',              title: tip('Raw SLA',            'HandledInSLA / (Offered − ShortAbandon)'), dataIndex: 'RawSLA',              width: 90,  render: p0,    sorter: (a, b) => num(a.RawSLA) - num(b.RawSLA),
      onCell: (r) => {
        if (r.RawSLA == null || r.TargetSLA == null) return {};
        const bg = Number(r.RawSLA) >= Number(r.TargetSLA) * 100 ? '#f6ffed' : '#fff1f0';
        return { style: { background: bg } };
      } },
    { key: 'AbandonPct',          title: tip('Abandon %',          'Abandon / (Offered − ShortAbandon)'), dataIndex: 'AbandonPct',          width: 95,  render: p1,    sorter: (a, b) => num(a.AbandonPct) - num(b.AbandonPct) },
    { key: 'RawASA',              title: tip('Raw ASA',            'Weighted avg speed of answer — SUM(ASA × Handled) / SUM(Handled), in seconds'), dataIndex: 'RawASA',              width: 90,  render: fmtMMSS, sorter: (a, b) => num(a.RawASA) - num(b.RawASA) },
    { key: 'PctOfOffered',        title: '% of Offered',       dataIndex: 'PctOfOffered',        width: 105, render: p1,    sorter: (a, b) => (a.PctOfOffered ?? 0) - (b.PctOfOffered ?? 0) },
    { key: 'OccupancyPct',        title: tip('Occupancy %',        '(IC_Active4SLA − IC_Available) / (IC_TotalDuration − IC_BreakLunch − IC_BreakUnscheduled)'), dataIndex: 'OccupancyPct',        width: 130, render: p1,    sorter: (a, b) => num(a.OccupancyPct)   - num(b.OccupancyPct) },
    { key: 'InAvailablePct',      title: tip('InAvailable %',      'IC_Available / (IC_TotalDuration − IC_BreakLunch − IC_BreakUnscheduled)'), dataIndex: 'InAvailablePct',      width: 115, render: p1,    sorter: (a, b) => num(a.InAvailablePct) - num(b.InAvailablePct) },
    { key: 'AvgShrinkPct',        title: tip('Avg Shrink %',       '(Staffed − IC_Active4SLA − IC_ProjectEmailSupport) / Staffed'), dataIndex: 'AvgShrinkPct',        width: 110, render: p1,    sorter: (a, b) => num(a.AvgShrinkPct) - num(b.AvgShrinkPct) },
    { key: 'AvgStaffed',          title: 'Avg Staffed',        dataIndex: 'AvgStaffed',          width: 100, render: d1,    sorter: (a, b) => num(a.AvgStaffed) - num(b.AvgStaffed) },
    { key: 'AvgIC4SLA',           title: 'Avg IC 4SLA',        dataIndex: 'AvgIC4SLA',           width: 100, render: d1,    sorter: (a, b) => num(a.AvgIC4SLA) - num(b.AvgIC4SLA) },
    { key: 'ErlangFTE',           title: 'Erlang FTE',         dataIndex: 'ErlangFTE',           width: 100, render: d2,    sorter: (a, b) => num(a.ErlangFTE) - num(b.ErlangFTE) },
    { key: 'ErlangShrinkageFTE',  title: 'Erlang Shrink FTE',  dataIndex: 'ErlangShrinkageFTE',  width: 135, render: d2,    sorter: (a, b) => num(a.ErlangShrinkageFTE) - num(b.ErlangShrinkageFTE) },
    { key: 'TargetSLA',           title: 'Target SLA',         dataIndex: 'TargetSLA',           width: 100, render: p0r01, sorter: (a, b) => num(a.TargetSLA) - num(b.TargetSLA) },
    { key: 'TargetTime',          title: 'Target Time (s)',    dataIndex: 'TargetTime',          width: 120, render: n,     sorter: (a, b) => num(a.TargetTime) - num(b.TargetTime) },
    { key: 'MaxOffered',          title: 'Max Offered',        dataIndex: 'MaxOffered',          width: 105, render: n,     sorter: (a, b) => num(a.MaxOffered) - num(b.MaxOffered) },
    { key: 'RawAHT',              title: tip('Raw AHT',            'Weighted avg handle time — SUM(AHT × Handled) / SUM(Handled), in minutes'), dataIndex: 'RawAHT',              width: 90,  render: fmtAHT, sorter: (a, b) => num(a.RawAHT) - num(b.RawAHT) },
    { key: 'AvgProjEmailSupport', title: 'Avg Proj Email',     dataIndex: 'AvgProjEmailSupport', width: 120, render: d2,    sorter: (a, b) => num(a.AvgProjEmailSupport) - num(b.AvgProjEmailSupport) },
    { key: 'AvgBreakLunch',       title: 'Avg Break/Lunch',    dataIndex: 'AvgBreakLunch',       width: 120, render: d2,    sorter: (a, b) => num(a.AvgBreakLunch) - num(b.AvgBreakLunch) },
    { key: 'AvgNotLoggedIn',      title: 'Avg Not Logged In',  dataIndex: 'AvgNotLoggedIn',      width: 135, render: d2,    sorter: (a, b) => num(a.AvgNotLoggedIn) - num(b.AvgNotLoggedIn) },
    { key: 'AvgTech',             title: 'Avg Tech',           dataIndex: 'AvgTech',             width: 90,  render: d2,    sorter: (a, b) => num(a.AvgTech) - num(b.AvgTech) },
    { key: 'AvgMeetCoach',        title: 'Avg Meet/Coach',     dataIndex: 'AvgMeetCoach',        width: 120, render: d2,    sorter: (a, b) => num(a.AvgMeetCoach) - num(b.AvgMeetCoach) },
    { key: 'AbandonLT5Sec',       title: 'Abandon LT 5s',     dataIndex: 'AbandonLT5Sec',       width: 115, render: n,     sorter: (a, b) => num(a.AbandonLT5Sec) - num(b.AbandonLT5Sec) },
    { key: 'Days',                title: 'Days',               dataIndex: 'Days',                width: 70,  render: n,     sorter: (a, b) => (a.Days || 0) - (b.Days || 0) },
    { key: 'ShortAbandon',        title: 'Short Abandon',      dataIndex: 'ShortAbandon',        width: 115, render: n,     sorter: (a, b) => num(a.ShortAbandon) - num(b.ShortAbandon) },
    { key: 'TotalOffered',        title: 'Offered',            dataIndex: 'TotalOffered',        width: 85,  render: n,     sorter: (a, b) => num(a.TotalOffered) - num(b.TotalOffered) },
    { key: 'TotalHandled',        title: 'Handled',            dataIndex: 'TotalHandled',        width: 85,  render: n,     sorter: (a, b) => num(a.TotalHandled) - num(b.TotalHandled) },
    { key: 'TotalHandledInSLA',   title: 'Handled In SLA',     dataIndex: 'TotalHandledInSLA',   width: 120, render: n,     sorter: (a, b) => num(a.TotalHandledInSLA) - num(b.TotalHandledInSLA) },
    { key: 'TotalAbandon',        title: 'Abandon',            dataIndex: 'TotalAbandon',        width: 85,  render: n,     sorter: (a, b) => num(a.TotalAbandon) - num(b.TotalAbandon) },
  ].map(toResizable), [toResizable]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Campaign Erlang Stats</Title>
        <Segmented
          options={[
            { label: 'Erlang Stats', value: 'standard' },
            { label: 'Trend', value: 'trend' },
            { label: 'Single Date', value: 'date-analysis' },
          ]}
          value={activeView}
          onChange={handleViewChange}
        />
      </div>

      <Form layout="inline" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        {activeView !== 'date-analysis' ? (
          <Form.Item label="Date Range">
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              allowClear={false}
            />
          </Form.Item>
        ) : (
          <>
            <Form.Item label="Date">
              <DatePicker
                value={analysisDate}
                onChange={(d) => { setAnalysisDate(d); }}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Form.Item>
            <Form.Item
              label={
                <Tooltip title="For this same Day of Week, how many weeks to look back to calculate the avg">
                  DOW Look back (wks)
                </Tooltip>
              }
            >
              <Select
                options={LOOKBACK_OPTIONS}
                value={lookbackWeeks}
                onChange={setLookbackWeeks}
                style={{ width: 80 }}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          label="Campaign"
          required
          validateStatus={!campaign ? 'error' : ''}
          help={!campaign ? 'Select a campaign to search' : ''}
        >
          <Select
            showSearch
            allowClear
            loading={campaignsLoading}
            placeholder={campaignsLoading ? 'Loading campaigns…' : 'Select a campaign'}
            options={filteredCampaignOpts}
            value={campaign}
            onChange={setCampaign}
            style={{ minWidth: 220 }}
            filterOption={(input, opt) =>
              String(opt.label).toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {activeView !== 'date-analysis' && (
          <>
            <Form.Item label="Call Hour">
              <Select
                mode="multiple"
                allowClear
                placeholder="All hours"
                options={HOUR_OPTIONS}
                value={callHours}
                onChange={setCallHours}
                style={{ minWidth: 160 }}
                maxTagCount="responsive"
              />
            </Form.Item>

            <Form.Item label="Day of Week">
              <Select
                mode="multiple"
                allowClear
                placeholder="All days"
                options={DAY_OPTIONS}
                value={dayOfWeek}
                onChange={setDayOfWeek}
                style={{ minWidth: 160 }}
                maxTagCount="responsive"
              />
            </Form.Item>

            <Form.Item label="Holiday">
              <Select
                value={isHoliday}
                onChange={setIsHoliday}
                options={[
                  { label: 'All',          value: 'all' },
                  { label: 'Holiday only', value: '1'   },
                  { label: 'Non-holiday',  value: '0'   },
                ]}
                style={{ width: 140 }}
              />
            </Form.Item>
          </>
        )}

        <Form.Item>
          <Tooltip title={!campaign ? 'Select a campaign first' : ''}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={loading}
              disabled={!campaign}
              onClick={activeView !== 'date-analysis' ? handleSearch : () => handleAnalysisSearch()}
            >
              Search
            </Button>
          </Tooltip>
        </Form.Item>

        {activeView === 'date-analysis' && (
          <Form.Item>
            {lookbackRows.length > 0 && (
              <Checkbox checked={hideAverages} onChange={(e) => setHideAverages(e.target.checked)} style={{ marginRight: 12 }}>
                Hide lookback avgs
              </Checkbox>
            )}
            <span style={{ fontSize: 11, color: '#8c8c8c' }}>Holidays are excluded in lookback when searching a non-holiday.</span>
          </Form.Item>
        )}
      </Form>

      {activeView === 'standard' && (
        <>
          <div style={{ marginBottom: 8, textAlign: 'right' }}>
            <Button
              icon={<DownloadOutlined />}
              size="small"
              disabled={processedRows.length === 0}
              onClick={handleExport}
            >
              Export to Excel
            </Button>
          </div>
          <Table
            size="small"
            rowKey="_key"
            loading={loading}
            dataSource={processedRows}
            columns={columns}
            components={{ header: { cell: ResizableTitle } }}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{ defaultPageSize: 200, showSizeChanger: true, pageSizeOptions: ['100', '200', '500'] }}
            bordered
          />
        </>
      )}

      {activeView === 'trend' && <ErlangTrend data={trendData} loading={loading} />}

      {activeView === 'date-analysis' && (
        <Table
          size="small"
          rowKey="_key"
          loading={loading}
          dataSource={analysisData.data}
          columns={analysisData.cols}
          sticky
          scroll={{ x: 'max-content' }}
          pagination={false}
          bordered
          locale={{ emptyText: 'Select a date and campaign, then click Search.' }}
        />
      )}
    </div>
  );
}
